import {
  type DatabaseProjectionColumn,
  databaseProjectionDefaultGroups,
  databaseProjectionGroupValues,
} from '@streamlain/database-identity';
import { addDays, startOfDay } from 'date-fns';

import { localDateOfDay } from '../../property-presets/date/calendar-day.js';
import type { TypeInstance } from '../logical/type.js';
import { t } from '../logical/type-presets.js';
import { createUniComponentFromWebComponent } from '../utils/uni-component/uni-component.js';
import {
  formatDateGroupDayLabel,
  formatDateGroupMonthLabel,
  formatDateGroupRangeLabel,
  formatDateGroupYearLabel,
  formatRelativeDateGroupLabel,
} from './date-label.js';
import { BooleanGroupView } from './renderer/boolean-group.js';
import { DateGroupView } from './renderer/date-group.js';
import { NumberGroupView } from './renderer/number-group.js';
import { SelectGroupView } from './renderer/select-group.js';
import { StringGroupView } from './renderer/string-group.js';
import type { GroupByConfig } from './types.js';

export const createGroupByConfig = <
  Data extends Record<string, unknown>,
  MatchType extends TypeInstance,
  GroupValue = unknown,
>(
  config: GroupByConfig<Data, MatchType, GroupValue>
): GroupByConfig => config as never;

export const ungroups = {
  key: 'Ungroups',
  value: null,
};

function groupColumn(type: TypeInstance): DatabaseProjectionColumn {
  const valueType = t.array.is(type) ? type.element : type;
  const options = t.tag.is(valueType) ? valueType.data : undefined;
  return {
    id: '',
    name: '',
    type: holdsDays(valueType) ? 'date' : '',
    data: { options },
    logicalType: null,
  };
}

function holdsDays(type: TypeInstance): boolean {
  return t.date.is(type) && type.data?.day === true;
}

/** The local `Date` a date group label shows: a day on its calendar day. */
function groupDate(type: TypeInstance, value: number): Date {
  return holdsDays(type) ? localDateOfDay(value) : new Date(value);
}

function groupValues(name: string, value: unknown, type: TypeInstance) {
  return databaseProjectionGroupValues(
    groupColumn(type),
    { type: 'groupBy', columnId: '', name },
    value,
    Date.now()
  );
}

function buildDateCfg(
  name: string,
  groupName: (date: Date | null) => string
): GroupByConfig {
  return createGroupByConfig({
    name,
    matchType: t.date.instance(),
    groupName: (type, v: number | null) =>
      groupName(v == null ? null : groupDate(type, v)),
    defaultKeys: _t => [ungroups],
    valuesGroup: (v: number | null, type) =>
      groupValues(name, v, type) as { key: string; value: number | null }[],
    addToGroup: (grp: number | null, _old: number | null) => grp,
    view: createUniComponentFromWebComponent(DateGroupView),
  });
}

const dateRelativeCfg = buildDateCfg('date-relative', d =>
  d ? formatRelativeDateGroupLabel(startOfDay(d), startOfDay(new Date())) : ''
);

const dateDayCfg = buildDateCfg('date-day', d =>
  d ? formatDateGroupDayLabel(d) : ''
);

const dateWeekSunCfg = buildDateCfg('date-week-sun', d =>
  d ? formatDateGroupRangeLabel(d, addDays(d, 6)) : ''
);

const dateWeekMonCfg = buildDateCfg('date-week-mon', d =>
  d ? formatDateGroupRangeLabel(d, addDays(d, 6)) : ''
);

const dateMonthCfg = buildDateCfg('date-month', d =>
  d ? formatDateGroupMonthLabel(d) : ''
);

const dateYearCfg = buildDateCfg('date-year', d =>
  d ? formatDateGroupYearLabel(d) : ''
);

export const groupByMatchers: GroupByConfig[] = [
  createGroupByConfig({
    name: 'select',
    matchType: t.tag.instance(),
    groupName: (type, value: string | null) => {
      if (t.tag.is(type) && type.data)
        return type.data.find(v => v.id === value)?.value ?? '';
      return '';
    },
    // Populated options first, "No {prop}" last. `sortByManually` only
    // applies a manual order when one is stored on the view; for a
    // first-render the natural defaultKeys order decides the kanban
    // column layout + group-by settings list. Putting the empty lane
    // at the end matches the task-tracker convention (populated
    // lanes are the work; "no value" is a pen for orphan rows).
    defaultKeys: type =>
      databaseProjectionDefaultGroups(groupColumn(type), 'select') as never,
    valuesGroup: (value, type) => groupValues('select', value, type) as never,
    addToGroup: (v: string | null, _old: string | null) => v,
    view: createUniComponentFromWebComponent(SelectGroupView),
  }),

  createGroupByConfig({
    name: 'multi-select',
    matchType: t.array.instance(t.tag.instance()),
    groupName: (type, value: string | null) => {
      if (t.array.is(type) && t.tag.is(type.element) && type.element.data)
        return type.element.data.find(v => v.id === value)?.value ?? '';
      return '';
    },
    // `ungroups` last — same rationale as `select` above.
    defaultKeys: type =>
      databaseProjectionDefaultGroups(
        groupColumn(type),
        'multi-select'
      ) as never,
    valuesGroup: (value, type) =>
      groupValues('multi-select', value, type) as never,
    addToGroup: (
      value: string | null,
      old: string[] | null
    ): string[] | null => {
      if (value == null) return old;
      return Array.isArray(old) ? [...old, value] : [value];
    },
    removeFromGroup: (value, old) =>
      Array.isArray(old) ? old.filter(v => v !== value) : old,
    view: createUniComponentFromWebComponent(SelectGroupView),
  }),

  createGroupByConfig({
    name: 'text',
    matchType: t.string.instance(),
    groupName: (_t, v) => `${v ?? ''}`,
    defaultKeys: type =>
      databaseProjectionDefaultGroups(groupColumn(type), 'text') as never,
    valuesGroup: (value, type) => groupValues('text', value, type) as never,
    addToGroup: (v: string | null, _old: string | null) => v,
    view: createUniComponentFromWebComponent(StringGroupView),
  }),

  createGroupByConfig({
    name: 'number',
    matchType: t.number.instance(),
    groupName: (_t, v) => `${v ?? ''}`,
    defaultKeys: type =>
      databaseProjectionDefaultGroups(groupColumn(type), 'number') as never,
    valuesGroup: (value, type) => groupValues('number', value, type) as never,
    addToGroup: (v: number | null, _old: number | null) =>
      typeof v === 'number' ? v * 10 : null,
    view: createUniComponentFromWebComponent(NumberGroupView),
  }),

  createGroupByConfig({
    name: 'boolean',
    matchType: t.boolean.instance(),
    groupName: (_t, v) => `${v?.toString() ?? ''}`,
    // `ungroups` last — matches the select/multi-select convention.
    defaultKeys: type =>
      databaseProjectionDefaultGroups(groupColumn(type), 'boolean') as never,
    valuesGroup: (value, type) => groupValues('boolean', value, type) as never,
    addToGroup: (v: boolean | null, _old: boolean | null) => v,
    view: createUniComponentFromWebComponent(BooleanGroupView),
  }),

  dateRelativeCfg,
  dateDayCfg,
  dateWeekSunCfg,
  dateWeekMonCfg,
  dateMonthCfg,
  dateYearCfg,
];
